import React, { useState } from 'react';
import {
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    signOut,
    setPersistence,
    browserSessionPersistence,
    browserLocalPersistence
} from 'firebase/auth';
import { Button, Form, Message, Grid, Segment, Header, Container, Modal, Label, Checkbox } from 'semantic-ui-react';
import { Link, useNavigate } from 'react-router-dom';
import "./Login.css"
import { analytics, auth, logEvent } from '../firebaseConfig'; // Assuming you have initialized firebase and exported auth from firebaseConfig
import { useTranslation } from 'react-i18next';
import SignUp from './SignUp';
import { usePostHog } from '@posthog/react';

function Login({ onSuccess, onCancel, contextMessage, initialMode = 'login', showGuestButton = false }) {
    const { t } = useTranslation();
    const posthog = usePostHog();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    const [loginOrSignUp, setLoginOrSignUp] = useState(initialMode);

    const getUserFriendlyError = (errorCode) => {
        switch (errorCode) {
            case 'auth/user-not-found':
                return t('UserNotFound');
            case 'auth/wrong-password':
                return t('WrongPassword');
            case 'auth/invalid-email':
                return t('InvalidEmail');
            case 'auth/user-disabled':
                return t('UserDisabled');
            case 'auth/too-many-requests':
                return t('TooManyRequests');
            default:
                return t('LogInFailed');
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        if (analytics) logEvent(analytics, 'user_try_login');
        setError('');
        setLoading(true);
        try {
            // Set persistence based on rememberMe
            const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
            await setPersistence(auth, persistence);

            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;
            if (!user.emailVerified) {
                await signOut(auth);
                setError(t('PleaseVerifyEmail'));
                return;
            }
            posthog?.identify(user.uid, { email: user.email });
            posthog?.capture('user_logged_in', { method: 'email' });
            if (onSuccess) {
                if (analytics) logEvent(analytics, 'user_managed_login');
                onSuccess();
            } else {
                navigate('/');
            }
        } catch (error) {
            posthog?.capture('user_login_failed', { method: 'email', error_code: error.code });
            setError(getUserFriendlyError(error.code));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
    if (analytics) logEvent(analytics, 'user_try_google_login');
        setGoogleLoading(true);
        const provider = new GoogleAuthProvider();
        try {
            // Set persistence based on rememberMe
            const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
            await setPersistence(auth, persistence);

            const googleCredential = await signInWithPopup(auth, provider);
            posthog?.identify(googleCredential.user.uid, { email: googleCredential.user.email });
            posthog?.capture('user_google_login');
            if (onSuccess) {
                if (analytics) logEvent(analytics, 'user_managed_google_login');
                onSuccess();
            } else {
                navigate('/');
            }
        } catch (error) {
            posthog?.capture('user_login_failed', { method: 'google', error_code: error.code });
            setError(getUserFriendlyError(error.code));
        } finally {
            setGoogleLoading(false);
        }
    };

    return (
        loginOrSignUp === 'signup' ? (
            <SignUp onSuccess={onSuccess} onCancel={onCancel} contextMessage={contextMessage} />
        ) : (
            <Modal open={true} onClose={onCancel} size='small' closeIcon>
                <Modal.Header style={{ backgroundColor: '#14b8a6', color: 'white' }}>{t('LogIn')}</Modal.Header>
                <Modal.Content>
                    {contextMessage && (
                        <Message style={{ backgroundColor: '#f3f4f6', borderColor: '#14b8a6', color: '#14b8a6' }}>
                            <p>{contextMessage}</p>
                        </Message>
                    )}
                    {showGuestButton && (
                        <Button
                            basic
                            fluid
                            size="large"
                            onClick={onCancel}
                            style={{ marginBottom: '1rem' }}
                        >
                            {t('continueAsGuest')}
                        </Button>
                    )}
                    <Form onSubmit={handleLogin} size="large">
                        <Form.Input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            fluid
                            icon="mail"
                            iconPosition="left"
                            placeholder="Email"
                            type="email"
                        />
                        <Form.Input
                            value={password}
                            onChange={(e) =>
                                setPassword(e.target.value)
                            }
                            fluid
                            icon="lock"
                            iconPosition="left"
                            placeholder="Password"
                            type={showPassword ? "text" : "password"}
                            action={{
                              icon: showPassword ? "eye slash" : "eye",
                              onClick: () => setShowPassword(!showPassword),
                              type: "button"
                            }}
                        />
                        <Form.Field style={{ marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Checkbox
                                    label={t('RememberMe')}
                                    checked={rememberMe}
                                    onChange={(_, data) => setRememberMe(data.checked)}
                                />
                                <Link to="/forgot-password" style={{ color: '#14b8a6' }}>
                                    {t('ForgotPassword')}
                                </Link>
                            </div>
                        </Form.Field>
                        <Button
                            type="submit"
                            color="teal"
                            fluid
                            size="large"
                            loading={loading}
                            disabled={loading}
                        >
                            {t('LogIn')}
                        </Button>
                    </Form>
                    <Button
                        color="google plus"
                        fluid
                        size="large"
                        onClick={handleGoogleSignIn}
                        style={{ marginTop: '1rem' }}
                        loading={googleLoading}
                        disabled={googleLoading}
                    >
                        {t('LogInGoogle')}
                    </Button>
                    <Message>
                        {t('NoAccount')}
                        <Button onClick={() => setLoginOrSignUp('signup')} color="teal">
                            {t('SignUp')}
                        </Button>
                    </Message>
                </Modal.Content>
            </Modal>)
    )
}

export default Login;